import { useCallback, useEffect, useState } from 'react'
import { AIModel } from '../../types/modelTypes'

// A new key: the old one held a UUID, which would now be sent as a model name
// and refused. Renaming it makes the upgrade invisible to a returning user.
const STORAGE_KEY = 'selected_model_name'

/**
 * True when a stored pick is no longer in the catalog the tenant offers.
 *
 * An empty catalog means "not known yet" (still loading, or model selection is
 * off), not "everything was removed", so the stored pick stands there.
 */
export function isStalePick(storedModel: string | undefined, offeredModels: AIModel[] | undefined): boolean {
  if (!storedModel || !offeredModels?.length) return false
  return !offeredModels.some(model => model.name === storedModel)
}

/**
 * The model the user picked, or undefined to let the tenant default answer.
 *
 * A pick the tenant no longer offers is dropped — from the returned value and
 * from storage — so the picker and the outgoing request agree. Keeping it
 * would show the default in the picker while still sending the removed name
 * as an override, and the backend refuses that.
 */
export function useSelectedModel(offeredModels?: AIModel[]) {
  const [selectedModel, setSelectedModel] = useState<string | undefined>(() => {
    return localStorage.getItem(STORAGE_KEY) || undefined
  })

  const isStale = isStalePick(selectedModel, offeredModels)

  useEffect(() => {
    if (isStale) {
      localStorage.removeItem(STORAGE_KEY)
      setSelectedModel(undefined)
    }
  }, [isStale])

  const selectModel = useCallback((model: string | undefined) => {
    setSelectedModel(model)
    if (model) {
      localStorage.setItem(STORAGE_KEY, model)
    } else {
      localStorage.removeItem(STORAGE_KEY)
    }
  }, [])

  // Report the stale pick as unset from the first render that can see the
  // catalog, so nothing is sent before the effect clears it.
  return { selectedModel: isStale ? undefined : selectedModel, selectModel }
}
